import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChannelBreakdownCard,
  ChannelEntry,
} from '../../components/cards/ChannelBreakdownCard'
import { ChartCard } from '../../components/cards/ChartCard'
import CountCard from '../../components/cards/CountCard'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import { TabNav } from '../../components/nav/tabNav'
import { ProgressBar } from '../../components/progressBar'
import { MiniHeader } from '../../components/typography/miniHeader'
import { PageHeader } from '../../components/typography/PageHeader'
import { TrendText } from '../../components/typography/trendText'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import { PeriodTextMap, ReportPaymentMethodsMap } from '../../constants'
import { formatCurrency } from '../../helpers'
import { useAppSelector } from '../../hooks'
import { usePermissions } from '../../hooks/usePermissions'
import { useSideNav } from '../../hooks/useSideNav'
import Layout from '../../layout'
import {
  useGetDashboardPercentChangesQuery,
  useGetDashboardReportQuery,
} from '../../redux-toolkits/reports/reports.slice'
import { DashboardReportParams } from '../../redux-toolkits/reports/reports.type'
import { CategoriesReportView } from './views/categories'
import { CreatorsReportView } from './views/creators'
import { CustomersReportView } from './views/customers'
import { ProductsReportView } from './views/products'
import { SalesRepReportView } from './views/salesRep'
import { TopCreatorsByChannelView } from './views/topCreatorsByChannel'

type ChannelReport = 'online' | 'physical'

type TableView =
  | 'creators'
  | 'customers'
  | 'products'
  | 'sales_rep'
  | 'categories'
  | 'top_creators_by_channel'

export const DashboardPage: React.FC = () => {
  const [dashboardReportQuery, setDashboardReportQuery] =
    useState<DashboardReportParams>({
      filter: 'today',
      isCustomDateRange: false,
      startDate: undefined,
      endDate: undefined,
    })
  const [tableView, setTableView] = useState<TableView>('creators')
  const [channelReportType, setChannelReportType] =
    useState<ChannelReport>('online')

  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)

  const navigate = useNavigate()

  const { hasPermissions } = usePermissions()

  const { navItems } = useSideNav()

  const { data: dashboardReport, isFetching: loadingDashboardReport } =
    useGetDashboardReportQuery(
      {
        ...dashboardReportQuery,
        fulfilmentCentre:
          selectedFulfilmentCenters.map((c) => c._id).join(',') || undefined,
      },
      {
        refetchOnMountOrArgChange: true,
        skip:
          dashboardReportQuery.isCustomDateRange &&
          (dashboardReportQuery.startDate === undefined ||
            dashboardReportQuery.endDate === undefined),
      },
    )

  const { data: dashboardPercentChanges } = useGetDashboardPercentChangesQuery(
    {
      ...dashboardReportQuery,
      fulfilmentCentre:
        selectedFulfilmentCenters.map((c) => c._id).join(',') || undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    if (!hasPermissions(['view_dashboard'])) {
      const visibleNavs = navItems?.filter((n) => !n.hidden)
      navigate(visibleNavs[0]?.href || visibleNavs[0]?.subMenu[0]?.href)
    }
  }, [navItems])

  const payload = dashboardReport?.data?.channelMetrics
    ?.filter(
      (v) =>
        v.channel !== 'POP' &&
        v.channel !== 'ANDROID_MOBILE' &&
        v.channel !== 'GOOD_HOUSE_PHYSICAL' &&
        v.channel !== 'IOS_MOBILE',
    )
    ?.map((channel) => {
      return {
        fulfilmentCentreName: 'Good Online',
        totalOrderValue: channel.amount,
        totalOrders: channel.orderCount,
        totalCreators: channel.uniqueVendorCount,
      }
    })

  const mainPayload =
    [
      ...(payload ?? []),
      ...(dashboardReport?.data?.fulfilmentCentreMetrics?.map((x) => ({
        fulfilmentCentreName: x?.fulfilmentCentreName,
        totalOrderValue: x?.totalOrderValue,
        totalOrders: x?.totalOrders,
        totalCreators: x?.uniqueVendorCount,
      })) ?? []),
    ].sort((a, b) => b.totalOrderValue - a.totalOrderValue) ?? []

  const channelEntries: ChannelEntry[] = mainPayload.map((fc) => ({
    label: fc.fulfilmentCentreName ?? '',
    amount: fc.totalOrderValue ?? 0,
    orderCount: fc.totalOrders ?? 0,
    creatorCount: fc.totalCreators ?? 0,
    subItems:
      fc.fulfilmentCentreName === 'Good Online'
        ? dashboardReport?.data?.storefrontMetrics
            ?.filter((x) => !x.requestDomain.includes('GOOD_HOUSE_PHYSICAL'))
            ?.map((item) => ({
              label: item.requestDomain,
              amount: item.amount,
              orderCount: item.orderCount,
            }))
        : undefined,
  }))

  return (
    <Layout>
      <div className="flex flex-col gap-10 w-full">
        {/* Header  */}
        <div className="flex items-center gap-4 flex-wrap justify-between">
          <PageHeader
            title="How Things Are Going"
            className="min-w-fit !mb-0"
          />

          <DateFilterNav
            filter={dashboardReportQuery.filter ?? 'custom'}
            isCustomDateRange={dashboardReportQuery.isCustomDateRange ?? false}
            startDate={dashboardReportQuery.startDate}
            endDate={dashboardReportQuery.endDate}
            setFilter={(filter) => {
              setDashboardReportQuery({
                ...dashboardReportQuery,
                filter: filter.filter,
                isCustomDateRange: filter.isCustomDateRange,
                endDate: filter.endDate,
                startDate: filter.startDate,
              })
            }}
          />
        </div>

        {/* Cards  */}
        <div className="w-full flex flex-col gap-6">
          <CountCardContainer className="!my-0">
            <CountCard
              count={dashboardReport?.data?.totalOrderValue ?? 0}
              text="Total Order Value"
              isCurrency
              loading={loadingDashboardReport}
            >
              <TrendText
                value={
                  dashboardPercentChanges?.data
                    ?.totalOrderValuePercentageChange ?? 0
                }
                suffixText={
                  dashboardReportQuery.isCustomDateRange
                    ? ' '
                    : `in the past ${
                        PeriodTextMap[dashboardReportQuery.filter!]
                      }`
                }
              />
            </CountCard>

            <CountCard
              count={dashboardReport?.data?.totalGoodRevenue ?? 0}
              text="Good's Revenue"
              isCurrency
              loading={loadingDashboardReport}
            >
              <TrendText
                value={
                  dashboardPercentChanges?.data
                    ?.totalGoodRevenuePercentageChange ?? 0
                }
                suffixText={
                  dashboardReportQuery.isCustomDateRange
                    ? ' '
                    : `in the past ${
                        PeriodTextMap[dashboardReportQuery.filter!]
                      }`
                }
              />
            </CountCard>

            <CountCard
              count={dashboardReport?.data?.totalCreatorSettlement ?? 0}
              text="Creator Settlement"
              isCurrency
              loading={loadingDashboardReport}
            >
              <TrendText
                value={
                  dashboardPercentChanges?.data
                    ?.totalCreatorSettlementPercentageChange ?? 0
                }
                suffixText={
                  dashboardReportQuery.isCustomDateRange
                    ? ' '
                    : `in the past ${
                        PeriodTextMap[dashboardReportQuery.filter!]
                      }`
                }
              />
            </CountCard>

            <CountCard
              count={dashboardReport?.data?.totalOrders ?? 0}
              text="Total Orders"
              isCurrency={false}
              valueSuffix="Orders"
              loading={loadingDashboardReport}
            >
              <TrendText
                value={
                  dashboardPercentChanges?.data?.totalOrdersPercentageChange ??
                  0
                }
                suffixText={
                  dashboardReportQuery.isCustomDateRange
                    ? ' '
                    : `in the past ${
                        PeriodTextMap[dashboardReportQuery.filter!]
                      }`
                }
              />
            </CountCard>

            <CountCard
              count={dashboardReport?.data?.uniqueCustomerCount ?? 0}
              text="Active Customers"
              isCurrency={false}
              valueSuffix={``}
              loading={loadingDashboardReport}
            >
              <TrendText
                value={
                  dashboardPercentChanges?.data
                    ?.customerCountPercentageChange ?? 0
                }
                suffixText={
                  dashboardReportQuery.isCustomDateRange
                    ? ' '
                    : `in the past ${
                        PeriodTextMap[dashboardReportQuery.filter!]
                      }`
                }
              />
            </CountCard>

            <CountCard
              count={dashboardReport?.data?.uniqueVendorCount ?? 0}
              text="Active Creators"
              isCurrency={false}
              valueSuffix={`/ ${dashboardReport?.data?.totalVendors ?? 0}`}
              loading={loadingDashboardReport}
            >
              <TrendText
                value={
                  dashboardPercentChanges?.data
                    ?.activeCreatorsPercentageChange ?? 0
                }
                suffixText={
                  dashboardReportQuery.isCustomDateRange
                    ? ' '
                    : `in the past ${
                        PeriodTextMap[dashboardReportQuery.filter!]
                      }`
                }
              />
            </CountCard>

            <CountCard
              count={dashboardReport?.data?.avgTimeToFulfill ?? 0}
              text="Average time to fulfil an order"
              isCurrency={false}
              valueSuffix="Minutes"
              loading={loadingDashboardReport}
            >
              <TrendText
                value={
                  dashboardPercentChanges?.data
                    ?.avgFulfilmentTimePercentageChange ?? 0
                }
                suffixText={
                  dashboardReportQuery.isCustomDateRange
                    ? ' '
                    : `in the past ${
                        PeriodTextMap[dashboardReportQuery.filter!]
                      }`
                }
                invertColor
              />
            </CountCard>

            <CountCard
              count={0}
              text="Third Party Settlement"
              isCurrency
              loading={loadingDashboardReport}
            />
          </CountCardContainer>

          <div className="grid grid-cols-1 md:grid-cols-2  gap-6 ">
            <ChannelBreakdownCard title="Channel" entries={channelEntries} />

            <ChartCard
              title="Payment Method"
              value={formatCurrency(
                dashboardReport?.data?.paymentMethodMetrics?.reduce(
                  (acc, d) => acc + d.amount,
                  0,
                ) ?? 0,
              )}
            >
              {dashboardReport?.data?.paymentMethodMetrics?.map((pm, idx) => {
                const total =
                  dashboardReport?.data?.paymentMethodMetrics?.reduce(
                    (acc, d) => acc + d.amount,
                    0,
                  )
                return (
                  <div
                    className="flex flex-col gap-2 py-4 border-b border-divider-gray last:border-b-0 last:pb-0"
                    key={`${idx}`}
                  >
                    <p>{`${ReportPaymentMethodsMap[pm.type]?.name}`}</p>

                    <p className="text-default-gray">
                      {formatCurrency(pm?.amount ?? 0)}
                    </p>

                    <ProgressBar
                      step={pm?.amount ?? 0}
                      total={total}
                      singleBarClassName={`${ReportPaymentMethodsMap[pm.type]
                        ?.color}`}
                      showPercent
                    />
                  </div>
                )
              })}
            </ChartCard>
          </div>
        </div>

        <MiniHeader name="Good Performance Tables" />

        <TabNav
          tabs={
            [
              {
                name: 'Creators',
                id: 'creators',
              },
              {
                name: 'Products',
                id: 'products',
              },
              {
                name: 'Customers',
                id: 'customers',
              },
              {
                name: 'Categories',
                id: 'categories',
              },
              {
                name: 'Sales Representatives',
                id: 'sales_rep',
              },
              {
                name: 'Top Creators by Channel',
                id: 'top_creators_by_channel',
              },
            ] as {
              name: string
              id: TableView
            }[]
          }
          type="nav"
          activeTab={tableView}
          setActiveTab={(tab) => {
            setTableView(tab as TableView)
          }}
        />

        {tableView === 'creators' && (
          <CreatorsReportView mainDashQuery={dashboardReportQuery} />
        )}
        {tableView === 'products' && (
          <ProductsReportView mainDashQuery={dashboardReportQuery} />
        )}
        {tableView === 'customers' && (
          <CustomersReportView mainDashQuery={dashboardReportQuery} />
        )}
        {tableView === 'categories' && (
          <CategoriesReportView mainDashQuery={dashboardReportQuery} />
        )}
        {tableView === 'sales_rep' && (
          <SalesRepReportView mainDashQuery={dashboardReportQuery} />
        )}
        {tableView === 'top_creators_by_channel' && (
          <TopCreatorsByChannelView mainDashQuery={dashboardReportQuery} />
        )}
      </div>
    </Layout>
  )
}
